import React, { useState } from 'react';
import { SimpleBarChart, SimpleLineChart, SimpleDonutChart } from './SimpleChart';
import { formatPercentage, formatNumber } from '../utils/consolidatedAnalytics.js';
import useCallSuccessAnalytics from '../hooks/useCallSuccessAnalytics';
import { CallSuccessRateSkeleton } from './AnalyticsLoadingSkeleton';
import AnalyticsErrorDisplay from './AnalyticsErrorDisplay';
import { isFallbackData, FallbackContent } from '../utils/analyticsFallbacks';
import { useTheme } from '../contexts/ThemeContext';
import { 
  createAccessibleTabPanelAttributes,
  createAccessibleLiveRegion,
  createAccessibleTabAttributes
} from '../utils/consolidatedAccessibility.js';
import { getWarningClasses } from '../utils/statusUtils.js';
import { getTouchFriendlyClasses } from '../utils/responsiveUtils.js';
import { getFocusClasses } from '../utils/consolidatedAccessibility.js';

const CallSuccessRateCard = ({ timeRange }) => {
  const { theme } = useTheme();
  const [selectedParrot, setSelectedParrot] = useState(null);
  const [selectedPhone, setSelectedPhone] = useState(null);
  const [activeView, setActiveView] = useState('overview'); // 'overview', 'parrots', 'phones', 'trends'
  
  const chartClasses = {
    container: 'w-full h-64 sm:h-80 lg:h-96',
    wrapper: 'relative bg-white dark:bg-gray-800 rounded-lg p-4 shadow'
  };
  const tableClasses = {
    container: 'overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg',
    table: 'min-w-full divide-y divide-gray-300 dark:divide-gray-700',
    header: 'bg-gray-50 dark:bg-gray-800',
    body: 'bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-800'
  };
  const warningClasses = getWarningClasses(theme);
  const touchClasses = getTouchFriendlyClasses();
  const liveRegionProps = createAccessibleLiveRegion('polite', false);
  
  const {
    callSuccessData,
    failureReasonsData,
    loading,
    error,
    retry,
    canRetry,
    isRetrying,
    isFallbackData: isUsingFallbackData
  } = useCallSuccessAnalytics(timeRange, selectedParrot, selectedPhone);

  if (loading) {
    return (
      <div 
        className={chartClasses.container}
        role="status"
        aria-label="Loading call success rate analysis"
      >
        <CallSuccessRateSkeleton />
        <span className="sr-only">Loading call success rate data...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className={chartClasses.container}>
        <h3 className={chartClasses.title} id="call-success-title">
          Call Success Rate Analysis
        </h3>
        <AnalyticsErrorDisplay
          error={error}
          onRetry={retry}
          canRetry={canRetry}
          isRetrying={isRetrying}
          role="alert"
          aria-live="assertive"
        />
      </div>
    );
  }

  if (!callSuccessData || !failureReasonsData) {
    return (
      <div 
        className={chartClasses.container}
        role="status"
        aria-label="Call success rate data unavailable"
      >
        <h3 className={chartClasses.title}>Call Success Rate Analysis</h3>
        <p className="text-gray-500 dark:text-gray-400 text-center py-8">
          No call success rate data available for the selected time period.
        </p>
      </div>
    );
  }

  const { overall, byParrot, byPhoneNumber, trends } = callSuccessData || {};
  const { failureBreakdown } = failureReasonsData || {};

  // Additional safety check for overall data
  if (!overall || typeof overall.successRate === 'undefined') {
    return (
      <div className={chartClasses.container}>
        <div className="text-center py-8">
          <div className="text-gray-400 mb-4">📊</div>
          <p className="text-gray-500 dark:text-gray-400">
            Call success rate data is still loading or unavailable.
          </p>
          {canRetry && (
            <button
              onClick={retry}
              disabled={isRetrying}
              className="mt-4 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 dark:text-white"
            >
              {isRetrying ? 'Retrying...' : 'Try Again'}
            </button>
          )}
        </div>
      </div>
    );
  }

  const viewTabs = [
    { id: 'overview', label: 'Overview', icon: '📊', description: 'Overall success rate metrics and failure breakdown' },
    { id: 'parrots', label: 'By Parrot', icon: '🦜', description: 'Success rates broken down by individual parrots' },
    { id: 'phones', label: 'By Phone', icon: '📞', description: 'Success rates broken down by phone numbers' },
    { id: 'trends', label: 'Trends', icon: '📈', description: 'Success rate trends over time' }
  ];
  
  const handleTabKeyDown = (event, tabId) => {
    const currentIndex = viewTabs.findIndex(tab => tab.id === tabId);
    let nextIndex;
    
    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        nextIndex = currentIndex > 0 ? currentIndex - 1 : viewTabs.length - 1;
        setActiveView(viewTabs[nextIndex].id);
        break;
      case 'ArrowRight':
        event.preventDefault();
        nextIndex = currentIndex < viewTabs.length - 1 ? currentIndex + 1 : 0;
        setActiveView(viewTabs[nextIndex].id);
        break;
      case 'Home':
        event.preventDefault();
        setActiveView(viewTabs[0].id);
        break;
      case 'End':
        event.preventDefault();
        setActiveView(viewTabs[viewTabs.length - 1].id);
        break;
    }
  };

  const renderOverview = () => (
    <div className="space-y-6">
      {/* Overall metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg dark:bg-gray-900">
          <div className="text-2xl font-bold text-teal-600 dark:text-teal-400">
            {formatPercentage(overall.successRate)}
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400 dark:text-gray-300 dark:text-gray-600">Success Rate</div>
        </div>
        <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg dark:bg-gray-900">
          <div className="text-2xl font-bold text-green-600 dark:text-green-400">
            {formatNumber(overall.successfulCalls)}
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400 dark:text-gray-300 dark:text-gray-600">Successful Calls</div>
        </div>
        <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg dark:bg-gray-900">
          <div className="text-2xl font-bold text-red-600 dark:text-red-400">
            {formatNumber(overall.failedCalls)}
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400 dark:text-gray-300 dark:text-gray-600">Failed Calls</div>
        </div>
      </div>

      {/* Failure reasons breakdown */}
      {failureBreakdown && failureBreakdown.length > 0 && (
        <div className="mt-6">
          <SimpleDonutChart
            data={failureBreakdown}
            title="Failure Reasons"
            labelKey="reason"
            valueKey="count"
            height={180}
          />
        </div>
      )}
    </div>
  );

  const renderParrotBreakdown = () => (
    <div className="space-y-4">
      {byParrot && byParrot.length > 0 ? (
        <>
          <SimpleBarChart
            data={byParrot.slice(0, 10)} // Show top 10 parrots
            title="Success Rate by Parrot"
            xKey="parrotName"
            yKey="successRate"
            height={200}
          />
          
          {/* Detailed table */}
          <div className="mt-6 overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700 dark:bg-gray-900">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Parrot
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Phone Number
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Total Calls
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Success Rate
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {byParrot.map((parrot, index) => (
                  <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-900">
                    <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">
                      {parrot.parrotName}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 dark:text-gray-300 dark:text-gray-600">
                      {parrot.phoneNumber || 'N/A'}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 dark:text-gray-300 dark:text-gray-600">
                      {formatNumber(parrot.totalCalls)}
                    </td>
                    <td className="px-4 py-2 text-sm">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        parrot.successRate >= 90 
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : parrot.successRate >= 70
                          ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                          : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                      }`}>
                        {formatPercentage(parrot.successRate)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          No parrot data available for the selected time period.
        </div>
      )}
    </div>
  );

  const renderPhoneBreakdown = () => (
    <div className="space-y-4">
      {byPhoneNumber && byPhoneNumber.length > 0 ? (
        <>
          <SimpleBarChart
            data={byPhoneNumber.slice(0, 10)} // Show top 10 phone numbers
            title="Success Rate by Phone Number"
            xKey="phoneNumber"
            yKey="successRate"
            height={200}
          />
          
          {/* Detailed table */}
          <div className="mt-6 overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700 dark:bg-gray-900">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Phone Number
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Friendly Name
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Total Calls
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Success Rate
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {byPhoneNumber.map((phone, index) => (
                  <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-900">
                    <td className="px-4 py-2 text-sm font-mono text-gray-900 dark:text-white">
                      {phone.phoneNumber}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 dark:text-gray-300 dark:text-gray-600">
                      {phone.phoneFriendlyName || 'N/A'}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 dark:text-gray-300 dark:text-gray-600">
                      {formatNumber(phone.totalCalls)}
                    </td>
                    <td className="px-4 py-2 text-sm">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        phone.successRate >= 90 
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : phone.successRate >= 70
                          ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                          : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                      }`}>
                        {formatPercentage(phone.successRate)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          No phone number data available for the selected time period.
        </div>
      )}
    </div>
  );

  const renderTrends = () => (
    <div className="space-y-4">
      {trends && trends.length > 0 ? (
        <SimpleLineChart
          data={trends}
          title="Success Rate Trend Over Time"
          xKey="date"
          yKey="successRate"
          height={250}
        />
      ) : (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          No trend data available for the selected time period.
        </div>
      )}
    </div>
  );

  const renderActiveView = () => {
    switch (activeView) {
      case 'overview':
        return renderOverview();
      case 'parrots':
        return renderParrotBreakdown();
      case 'phones':
        return renderPhoneBreakdown();
      case 'trends':
        return renderTrends();
      default:
        return renderOverview();
    }
  };

  return (
    <div 
      className={chartClasses.container}
      role="region"
      aria-labelledby="call-success-title"
    >
      {/* Live region for screen reader announcements */}
      <div {...liveRegionProps}>
        {activeView && `Viewing ${viewTabs.find(tab => tab.id === activeView)?.label} data`}
      </div>
      
      <div className={chartClasses.header}>
        <h3 
          id="call-success-title"
          className={chartClasses.title}
        >
          Call Success Rate Analysis
        </h3>
        
        {/* View tabs */}
        <div 
          className="flex flex-wrap space-x-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1"
          role="tablist"
          aria-label="Call success rate view options"
        >
          {viewTabs.map((tab) => {
            const isSelected = activeView === tab.id;
            const tabId = `success-tab-${tab.id}`;
            const panelId = `success-panel-${tab.id}`;
            const focusClasses = getFocusClasses(theme, 'primary');
            
            return (
              <button
                key={tab.id}
                onClick={() => setActiveView(tab.id)}
                onKeyDown={(e) => handleTabKeyDown(e, tab.id)}
                className={`
                  px-3 py-1 text-sm font-medium rounded-md transition-colors
                  ${touchClasses.button}
                  ${focusClasses}
                  ${isSelected
                    ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }
                `}
                {...createAccessibleTabAttributes(tabId, panelId, isSelected)}
                aria-describedby={`${tabId}-description`}
              >
                <span className="mr-1" aria-hidden="true">{tab.icon}</span>
                {tab.label}
                <span id={`${tabId}-description`} className="sr-only">
                  {tab.description}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Fallback data warning */}
      {isUsingFallbackData && (
        <div 
          className={`${warningClasses.container} mb-4`}
          role="alert"
          aria-live="polite"
        >
          <div className="flex items-center">
            <svg 
              className={warningClasses.icon} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <div className={warningClasses.content}>
              <p className={warningClasses.title}>
                Data Unavailable
              </p>
              <p className={warningClasses.message}>
                {callSuccessData?.message || 'Call success data is currently unavailable'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Tab panel content */}
      <div
        role="tabpanel"
        id={`success-panel-${activeView}`}
        aria-labelledby={`success-tab-${activeView}`}
        tabIndex={0}
      >
        {renderActiveView()}
      </div>
    </div>
  );
};

export default CallSuccessRateCard;
